import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { ApiSeasonTableSeasonIdGet200Response } from '@/api/livescore/generated';
import { livescoreClient } from '@/api/livescore/client';
import { useApp } from './AppContext';

interface LivescoreSeasonTableContextType {
  seasonTable: ApiSeasonTableSeasonIdGet200Response['data']['standings'] | null;
  fetchSeasonTable: () => Promise<void>;
  loading: boolean;
  type: Types;
  setType: (type: Types) => void;
  showForm: boolean;
  setShowForm: Dispatch<SetStateAction<boolean>>;
  outcomesUp: Map<string, string>;
  outcomesDown: Map<string, string>;
  seasonId: string;
  season: ApiSeasonTableSeasonIdGet200Response['data']['season'] | null;
}

const LivescoreSeasonTableContext = createContext<LivescoreSeasonTableContextType | undefined>(undefined);

type LivescoreSeasonTableProviderProps = {
  children: ReactNode;
  seasonId: string;
  leagueName?: string;
  initSeason?: ApiSeasonTableSeasonIdGet200Response['data']['season'] | undefined;
} & (
  | {
      initSeasonTable: ApiSeasonTableSeasonIdGet200Response['data']['standings'];
      initOutcomes: ApiSeasonTableSeasonIdGet200Response['data']['outcome'];
    }
  | {
      initSeasonTable?: never;
      initOutcomes?: never;
    }
);

export const types = ['total', 'home', 'away'] as const;
export type Types = (typeof types)[number];

export const LivescoreSeasonTableProvider: React.FC<LivescoreSeasonTableProviderProps> = ({
  children,
  seasonId,
  initSeasonTable,
  initOutcomes,
  leagueName,
  initSeason,
}) => {
  const { appendJsonLd } = useApp();
  const [seasonTable, setSeasonTable] = useState<ApiSeasonTableSeasonIdGet200Response['data']['standings'] | null>(
    initSeasonTable ?? null,
  );
  const [loading, setLoading] = useState(initSeasonTable ? false : true);
  const [type, setType] = useState<Types>('total');
  const [showForm, setShowForm] = useState(false);
  const [outcomesUp, setOutcomesUp] = useState<Map<string, string>>(
    initOutcomes
      ? new Map<string, string>(initOutcomes.up?.map(({ to, color }) => [to, color]) ?? [])
      : new Map<string, string>(),
  );
  const [outcomesDown, setOutcomesDown] = useState<Map<string, string>>(
    initOutcomes
      ? new Map<string, string>(initOutcomes.down?.map(({ to, color }) => [to, color]) ?? [])
      : new Map<string, string>(),
  );
  const [season, setSeason] = useState<ApiSeasonTableSeasonIdGet200Response['data']['season'] | null>(initSeason);

  const fetchSeasonTable = useCallback(async () => {
    setLoading(true);
    const seasonTableResponse = await livescoreClient.apiSeasonTableSeasonIdGet({ seasonId });

    if (seasonTableResponse.data.data.standings) {
      setSeasonTable(seasonTableResponse.data.data.standings);
      setOutcomesUp(
        new Map<string, string>(seasonTableResponse.data.data.outcome?.up?.map(({ to, color }) => [to, color]) ?? []),
      );
      setOutcomesDown(
        new Map<string, string>(seasonTableResponse.data.data.outcome?.down?.map(({ to, color }) => [to, color]) ?? []),
      );
      setSeason(seasonTableResponse.data.data.season);
    }

    setLoading(false);
  }, [seasonId]);

  useEffect(() => {
    const jsonLd: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'SportsOrganization',
      sport: 'Soccer',
    };

    if (leagueName) {
      jsonLd.name = leagueName;
    }

    if (season) {
      jsonLd.startDate = season.start_date;
      jsonLd.endDate = season.end_date;
      if (season.country) {
        jsonLd.location = {
          '@type': 'Country',
          name: season.country,
        };
      }
    }

    appendJsonLd(jsonLd);
  }, [leagueName, season?.name]);

  return (
    <LivescoreSeasonTableContext.Provider
      value={{
        seasonTable,
        fetchSeasonTable,
        loading,
        type,
        setType,
        showForm,
        setShowForm,
        outcomesUp,
        outcomesDown,
        seasonId,
        season,
      }}
    >
      {children}
    </LivescoreSeasonTableContext.Provider>
  );
};

export const useSeasonTable = () => {
  const context = useContext(LivescoreSeasonTableContext);
  if (!context) {
    throw new Error('useSeasonTable must be used within an LivescoreSeasonTableProvider');
  }
  return context;
};
